import { ShipmentGroup } from '../interfaces/Import/ShipmentGroupInterface'
import { ImportDetailDTO } from '../interfaces/Import/ShipmentBulkUploadApi'
import { detailPartnerId } from './shipmentPayload'

/**
 * Empareja las guías de un Excel corregido con las líneas en error de SU carga masiva.
 *
 * Es la vuelta del round-trip: el operador bajó la plantilla prellenada desde el detalle,
 * la corrigió en Excel y la vuelve a subir sobre la MISMA carga. Acá se decide qué fila del
 * archivo actualiza qué línea, y —sobre todo— qué NO se manda.
 *
 * La guarda es crítica: `ShipmentBulkUploadReview` en modo `updateImport` arma
 * `corrections[]` con `g.importDetailId ?? ''` sin filtrar ni validar, y el `PUT /update`
 * solo ACTUALIZA líneas existentes (no inserta). Una guía sin id viajaría como `''` dentro
 * del mismo lote y muy probablemente lo haría fallar entero. Por eso solo `matched` puede
 * llegar al editor.
 */

export interface CorrectionMatchResult {
  /** Guías del archivo que corresponden a una línea en error. Todas con `importDetailId`. */
  matched: ShipmentGroup[]
  /**
   * Guías del archivo que no corresponden a ninguna línea en error (agregadas a mano, o
   * con el ID borrado y un nº de guía que no existe en la carga). Se descartan y se avisan:
   * el backend no puede insertarlas en esta carga.
   */
  unmatched: ShipmentGroup[]
  /**
   * Líneas en error que el archivo NO trae: el operador borró esas filas, o dos líneas
   * compartían nº de guía aliada y el parser las agrupó en una sola. Quedan como están, en
   * error. Se avisan para que nadie crea que se corrigieron.
   */
  missing: ImportDetailDTO[]
  /** Nº de guía de las filas que reclamaban una línea ya tomada. Se queda la primera. */
  duplicated: string[]
  /** true si NINGUNA fila traía la columna de ID y hubo que emparejar por nº de guía. */
  usedTrackingFallback: boolean
}

const norm = (v?: string | null): string => (v ?? '').trim().toUpperCase()

/**
 * @param groups Guías reconstruidas por el parser del Excel. `importDetailId` viene de la
 * columna oculta de ID, cuando el archivo la trae.
 * @param errorDetails Detalles en error de la carga (75/77), los únicos corregibles.
 */
export const matchCorrectionGroups = (
  groups: ShipmentGroup[],
  errorDetails: ImportDetailDTO[]
): CorrectionMatchResult => {
  const byId = new Map<string, ImportDetailDTO>()
  const byTracking = new Map<string, ImportDetailDTO>()
  // Un nº de guía repetido entre las líneas en error no puede usarse como clave: no se
  // sabría cuál de las dos actualizar. Se marcan para NO emparejar por ahí.
  const ambiguousTracking = new Set<string>()

  errorDetails.forEach((d) => {
    byId.set(norm(d.shipment_bulk_upload_detail_id), d)
    // `detailPartnerId` y no `shipment_header_reference` a secas: es el MISMO
    // resolutor con el que se llenó la columna "Nº de guía aliada" de la plantilla
    // que el operador está devolviendo. Las filas que fallaron antes de crear la
    // guía tienen esa referencia vacía y solo traen el id dentro del payload; sin
    // este fallback, esas filas solo podían emparejar por la columna oculta de ID
    // y, si el operador la perdía, se descartaban en silencio.
    const t = norm(detailPartnerId(d))
    if (!t) return
    if (byTracking.has(t)) ambiguousTracking.add(t)
    else byTracking.set(t, d)
  })

  const matched: ShipmentGroup[] = []
  const unmatched: ShipmentGroup[] = []
  const duplicated: string[] = []
  const takenDetailIds = new Set<string>()
  let sawAnyId = false

  for (const g of groups) {
    const id = norm(g.importDetailId)
    if (id) sawAnyId = true

    // 1) Por ID (lo normal). 2) Sin ID, por nº de guía aliada, salvo que ese número esté
    //    repetido entre las líneas en error — ahí no hay forma de saber cuál es.
    const tracking = norm(g.shipmentHeaderReference)
    const detail = id
      ? byId.get(id)
      : tracking && !ambiguousTracking.has(tracking)
        ? byTracking.get(tracking)
        : undefined

    if (!detail) {
      unmatched.push(g)
      continue
    }

    if (takenDetailIds.has(detail.shipment_bulk_upload_detail_id)) {
      duplicated.push(g.shipmentHeaderReference || tracking)
      continue
    }

    takenDetailIds.add(detail.shipment_bulk_upload_detail_id)
    matched.push({
      ...g,
      // Se toma SIEMPRE del detalle, no de la celda: aunque la columna venga manipulada,
      // lo que viaja al backend es el id real de la línea que se emparejó.
      importDetailId: detail.shipment_bulk_upload_detail_id,
    })
  }

  const missing = errorDetails.filter(
    (d) => !takenDetailIds.has(d.shipment_bulk_upload_detail_id)
  )

  return {
    matched,
    unmatched,
    missing,
    duplicated,
    usedTrackingFallback: !sawAnyId && groups.length > 0,
  }
}

/**
 * Detalles en error alineados 1:1 con `matched`, para poder derivar los errores por campo
 * (`mapDetailExecutionError`) que el editor muestra como estado inicial: el Review los lee
 * por ÍNDICE del arreglo de grupos.
 */
export const detailsForMatched = (
  matched: ShipmentGroup[],
  errorDetails: ImportDetailDTO[]
): ImportDetailDTO[] => {
  const byId = new Map(
    errorDetails.map((d) => [d.shipment_bulk_upload_detail_id, d])
  )
  return matched.map((g) => byId.get(g.importDetailId || '')!).filter(Boolean)
}
